import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { FilterType, IFilter } from '../../../../interfaces/Filter.interfaces';
import { filterSlice } from '../../../../store/filterSlice';
import { setScrollAnimationExecuted } from '../../../../store/scrollYSlice';
import { FilterSlideModal } from '../FilterSlideModal/FilterSlideModal';
import { FilterButton } from './components/FilterButton';

interface FilterSectionProps {
  filterType: FilterType;
  style?: CSSProperties;
}

export const FilterSection = ({ filterType, style }: FilterSectionProps) => {
  const dispatch = useAppDispatch();
  const filterStates = useAppSelector((state) => state.filter[filterType]);
  const scrollAnimationExecuted = useAppSelector((state) => state.scrollY.scrollAnimationExecuted);
  const filterActions = filterSlice.actions;
  const { pathname } = useLocation();

  const [isShowFilterSlideModal, setIsShowFilterSlideModal] = useState<boolean>(false);
  const filterButtonContainerRef = useRef<HTMLDivElement>(null);

  // 활성화된 필터 개수 계산 - useCheckFilterButtonActive 로직 활용
  const activeFilterCount = useMemo(() => {
    let count = 0;

    filterStates.globalFilters.forEach((filter) => {
      let isFilterButtonActive = false;

      // useCheckFilterButtonActive와 동일한 로직 사용
      if (filter.optionType === 'radio' || filter.optionType === 'checkbox') {
        filter.options.forEach((option) => {
          if (option.isSelected && !option.isDefault) {
            isFilterButtonActive = true;
          }
        });
      } else if (filter.optionType === 'slider') {
        filter.options.forEach((option) => {
          if (option.value !== option.defaultValue) {
            isFilterButtonActive = true;
          }
        });
      }

      if (isFilterButtonActive) {
        count += 1;
      }
    });

    return count;
  }, [filterStates.globalFilters]);

  // 스크롤 애니메이션 효과 - filterType별로 1회만 실행
  useEffect(() => {
    // filterType과 pathname을 조합한 고유 키 생성
    const animationKey = `${filterType}-${pathname}`;

    // 이미 해당 filterType + pathname에서 애니메이션이 실행되었으면 스킵
    if (scrollAnimationExecuted[animationKey]) {
      return;
    }

    if (filterButtonContainerRef.current) {
      // 처음에 오른쪽으로 스크롤
      filterButtonContainerRef.current.scrollLeft = 100;

      // 0.2초 후 원래 위치로 부드럽게 스크롤
      setTimeout(() => {
        if (filterButtonContainerRef.current) {
          filterButtonContainerRef.current.scrollTo({
            left: 0,
            behavior: 'smooth',
          });
        }
      }, 400);

      // 해당 filterType + pathname에서 애니메이션이 실행되었음을 표시
      dispatch(setScrollAnimationExecuted({ pathname: animationKey, executed: true }));
    }
  }, [pathname, filterType, scrollAnimationExecuted, dispatch]);

  useEffect(() => {
    if (
      (filterType === 'marketPriceFilter' || filterType === 'directDealFilter') &&
      filterStates.selectedFilterKey &&
      filterStates.globalFilters.length > 0
    ) {
      const filterIndex = filterStates.globalFilters.findIndex(
        (filter) => filter.key === filterStates.selectedFilterKey,
      );
      if (filterIndex !== -1) {
        setIsShowFilterSlideModal(true);
        dispatch(filterActions.changeHaveToRefreshLocalStates({ filterType, value: true }));
        dispatch(filterActions.changeClickedFilterButtonIndex({ filterType, value: filterIndex }));
        dispatch(filterActions.setSelectedFilterKey({ filterType, value: null }));
      }
    }
  }, [filterStates.selectedFilterKey, filterStates.globalFilters, dispatch, filterType, filterActions]);

  const closeFilterSlideModal = () => {
    if (filterType === 'marketPriceFilter') {
      ampli.pvmCloseMarketPriceFilterModal({ is_auto_opened: filterStates.isAutoOpened });
      dispatch(filterActions.setIsAutoOpened({ filterType, value: false }));
    }
    setIsShowFilterSlideModal(false);
  };

  const handleFilterButtonClick = (filter: IFilter, filterIndex: number) => {
    if (filterType === 'productFilter') {
      ampli.pvmClickPlpProductFilterButton({ filter_key: filter.key });
      ReactGA.event('pvm_click_plp_product_filter_button', { filter_key: filter.key });
    }

    if (filterType === 'userOwnTractorFilter') {
      ampli.pvmClickPokeFilterButton({ filter_key: filter.key });
      ReactGA.event('pvm_click_poke_filter_button', { filter_key: filter.key });
    }

    if (filterType === 'marketPriceFilter') {
      ampli.pvmClickMarketPriceFilterButton({ filter_key: filter.key });
    }

    if (filterType === 'directDealFilter') {
      ampli.pvmClickOlpProductFilterButton({ filter_key: filter.key });
    }

    setIsShowFilterSlideModal(true);
    dispatch(filterActions.changeHaveToRefreshLocalStates({ filterType, value: true }));
    dispatch(filterActions.changeClickedFilterButtonIndex({ filterType, value: filterIndex }));
  };

  const handleFilterControlButtonClick = () => {
    ampli.pvmClickFilterIconButton({ filter_type: filterType, button_type: 'open_filter_modal' });

    setIsShowFilterSlideModal(true);
    dispatch(filterActions.changeHaveToRefreshLocalStates({ filterType, value: true }));
    // 필터 모달이 열릴 때 스크롤을 맨 위로 설정
    dispatch(filterActions.changeClickedFilterButtonIndex({ filterType, value: 0 }));
  };

  const handleResetFilterClick = () => {
    ampli.pvmClickFilterIconButton({ filter_type: filterType, button_type: 'reset_filter' });

    // 필터 초기화
    dispatch(filterActions.resetAllFilters({ filterType }));
  };

  return (
    <Container style={{ ...style }}>
      <FilterSectionBody>
        <FilterButtonContainer ref={filterButtonContainerRef}>
          {filterStates.globalFilters.map((filter, filterIndex) => {
            if (filter.key === 'rowConfig') {
              return null;
            }

            return (
              <FilterButton
                key={filter.key}
                filter={filter}
                onClick={() => handleFilterButtonClick(filter, filterIndex)}
                filterType={filterType}
              />
            );
          })}
        </FilterButtonContainer>

        <FilterControlButtonSection>
          <FilterControlButton onClick={handleFilterControlButtonClick}>
            <FilterIcon />
            {activeFilterCount > 0 && <FilterCountBadge>{activeFilterCount}</FilterCountBadge>}
          </FilterControlButton>
          {activeFilterCount > 0 && (
            <FilterControlButton onClick={handleResetFilterClick}>
              <ResetIcon />
            </FilterControlButton>
          )}
        </FilterControlButtonSection>
      </FilterSectionBody>

      <FilterSlideModal isShow={isShowFilterSlideModal} closeModal={closeFilterSlideModal} filterType={filterType} />
    </Container>
  );
};

const FilterIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M16.5 7H3.5C2.672 7 2 6.328 2 5.5C2 4.672 2.672 4 3.5 4H16.5C17.328 4 18 4.672 18 5.5C18 6.328 17.328 7 16.5 7Z"
        fill="#505363"
      />
      <path
        d="M14.5 12H5.5C4.672 12 4 11.328 4 10.5C4 9.672 4.672 9 5.5 9H14.5C15.328 9 16 9.672 16 10.5C16 11.328 15.328 12 14.5 12Z"
        fill="#505363"
      />
      <path
        d="M11.5 17H8.5C7.672 17 7 16.328 7 15.5C7 14.672 7.672 14 8.5 14H11.5C12.328 14 13 14.672 13 15.5C13 16.328 12.328 17 11.5 17Z"
        fill="#505363"
      />
    </svg>
  );
};

const ResetIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M3.971 7.36284L7.429 9.95584C8.076 10.4408 9 9.97884 9 9.16984V3.98384C9 3.17484 8.076 2.71284 7.429 3.19784L3.971 5.79084C3.447 6.18384 3.447 6.96984 3.971 7.36284Z"
        fill="#505363"
      />
      <path
        d="M9.885 5.5625H7C6.448 5.5625 6 6.0105 6 6.5625C6 7.1145 6.448 7.5625 7 7.5625H9.885C11.815 7.5625 13.385 9.1325 13.385 11.0625C13.385 12.9765 11.887 14.5135 9.989 14.5615C9.869 14.5625 7.055 14.5615 7.005 14.5625C7.003 14.5625 7.002 14.5615 7 14.5615C6.448 14.5615 6 15.0095 6 15.5615C6 16.1135 6.448 16.5615 7 16.5615C7 16.5615 9.987 16.5625 10.025 16.5615C13.03 16.4855 15.384 14.0705 15.384 11.0625C15.385 8.0295 12.917 5.5625 9.885 5.5625Z"
        fill="#505363"
      />
    </svg>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const FilterSectionBody = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 8px 0px var(--radius-large, 16px) 0px;
`;

const FilterButtonContainer = styled.div`
  display: flex;
  align-items: center;

  padding: 0px var(--radius-large, 16px);
  gap: var(--radius-medium, 8px);

  overflow-x: scroll;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const FilterControlButtonSection = styled.div`
  display: flex;
  height: 40px;
  padding: 2px 16px 2px 8px;
  align-items: center;
  gap: 8px;

  border-left: 1px solid var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  background: #fff;
`;

const FilterControlButton = styled.div`
  display: flex;
  width: 34px;
  height: 34px;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  aspect-ratio: 1/1;
  position: relative;
  cursor: pointer;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const FilterCountBadge = styled.div`
  display: flex;
  height: 16px;
  min-width: 16px;
  padding: 0 4px;
  justify-content: center;
  align-items: center;
  gap: 16px;

  position: absolute;
  right: -3px;
  top: -3px;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);

  color: #fff;
  text-align: center;

  /* System/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 10.2px */
`;
